import { SAMPLE_POLL_LANGUAGES, SamplePollLanguage } from '../poll/header.class';
// direkt aus der Datei, nicht ueber index.ts (Zirkularitaet)
import { isResolvableVoteSlug } from '../poll/vote-slug.utils';
import {
    SAMPLE_POLL_LIST_SEGMENT_BY_LANG,
    SAMPLE_POLL_MATRIX_SUFFIX_BY_LANG,
} from './sample-poll-seo.constants';

/**
 * Klassifikation eines Request-Pfads fuer die ogProxy Cloud Function.
 *
 * - `samplePoll`: Detail-Seite `/{lang}/{listSegment}/{slug}` oder deren
 *   Matrix-/Results-Ansicht `/{lang}/{listSegment}/{slug}/{matrixSuffix}`
 *   (bekommt dieselben Tags wie die Detail-Seite).
 * - `samplePollList`: bare Listing-Uebersicht `/{lang}/{listSegment}` —
 *   lokalisierte Listing-Metas, kein RTDB-Read.
 * - `vote`: `/vote/{ownerId}/{pollId}[/{voteUserId}]`, optional mit
 *   Sprach-Prefix (das URL-Segment `vote` ist in allen 6 Sprachen `vote`).
 * - `voteSlug`: `/vote/{slug}` (Teilnehmer-Link-Slug, Workorder S2), optional
 *   mit Sprach-Prefix — ein Segment, das `isResolvableVoteSlug` besteht;
 *   Aufrufer loest ueber `whoo/pollSlugs/{slug}` auf.
 * - `generic`: alles andere (unbekannte Pfade, unsichere Segmente) —
 *   Aufrufer liefert generische Tags, fail-closed.
 *   `family` sagt, unter welcher Rewrite-Familie der Pfad einsortiert war
 *   (steuert Cache-Control: vote -> private).
 */
export type OgRoute =
    | { kind: 'samplePoll'; lang: SamplePollLanguage; slug: string; isMatrix: boolean }
    | { kind: 'samplePollList'; lang: SamplePollLanguage }
    | { kind: 'vote'; lang: SamplePollLanguage | null; ownerId: string; pollId: string }
    | { kind: 'voteSlug'; lang: SamplePollLanguage | null; slug: string }
    | { kind: 'generic'; lang: SamplePollLanguage | null; family: 'samplePoll' | 'vote' | null };

const VOTE_SEGMENT = 'vote';

/**
 * RTDB-Keys duerfen keine `. # $ / [ ]` enthalten; wir erlauben nur das
 * Zeichenset, das Push-IDs und Slugs tatsaechlich nutzen. Alles andere
 * (inkl. Percent-Encoding) -> generic, bevor es in `ref()` landet.
 */
const SAFE_RTDB_KEY = /^[A-Za-z0-9_-]+$/;

export function isSafeRtdbKey(value: string): boolean {
    return SAFE_RTDB_KEY.test(value);
}

function toSamplePollLanguage(segment: string): SamplePollLanguage | null {
    return (SAMPLE_POLL_LANGUAGES as string[]).indexOf(segment) >= 0
        ? (segment as SamplePollLanguage)
        : null;
}

export function classifyOgProxyPath(path: string): OgRoute {
    const cleanPath = (path || '').split('?')[0].split('#')[0];
    const segments = cleanPath.split('/').filter((segment) => segment.length > 0);

    const lang = segments.length > 0 ? toSamplePollLanguage(segments[0]) : null;
    const rest = lang ? segments.slice(1) : segments;

    if (rest[0] === VOTE_SEGMENT) {
        // vote/{ownerId}/{pollId} oder vote/{ownerId}/{pollId}/{voteUserId} —
        // immer die ERSTEN zwei Segmente nach `vote` nehmen, nie die letzten.
        const keys = rest.slice(1);
        if (keys.length === 1) {
            // Teilnehmer-Link-Slug (S2): Format ok und nicht hart reserviert. Die weiche
            // Liste ist nur eine Bremse beim Anlegen und darf die Aufloesung eines
            // bestehenden Polls nicht blockieren. Ungueltig -> generic, kein RTDB-Read.
            const slug = keys[0];
            if (isResolvableVoteSlug(slug)) {
                return { kind: 'voteSlug', lang, slug };
            }
            return { kind: 'generic', lang, family: 'vote' };
        }
        if (keys.length < 2 || keys.length > 3) {
            return { kind: 'generic', lang, family: 'vote' };
        }
        const ownerId = keys[0];
        const pollId = keys[1];
        if (!isSafeRtdbKey(ownerId) || !isSafeRtdbKey(pollId)) {
            return { kind: 'generic', lang, family: 'vote' };
        }
        return { kind: 'vote', lang, ownerId, pollId };
    }

    if (lang && rest[0] === SAMPLE_POLL_LIST_SEGMENT_BY_LANG[lang]) {
        const tail = rest.slice(1);
        if (tail.length === 0) {
            // Listing-Uebersicht ohne Slug -> lokalisierte Listing-Metas, kein RTDB-Read
            return { kind: 'samplePollList', lang };
        }
        const slug = tail[0];
        const isMatrix = tail.length === 2 && tail[1] === SAMPLE_POLL_MATRIX_SUFFIX_BY_LANG[lang];
        if (tail.length > 2 || (tail.length === 2 && !isMatrix) || !isSafeRtdbKey(slug)) {
            return { kind: 'generic', lang, family: 'samplePoll' };
        }
        return { kind: 'samplePoll', lang, slug, isMatrix };
    }

    return { kind: 'generic', lang, family: null };
}
